import {
  Card,
  List,
  Empty,
  Typography,
  Divider,
  Select,
  Space,
  DatePicker,
  Tag,
  Avatar,
  Input,
  Tooltip,
} from "antd";
import {
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  UserOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useState, useMemo } from "react";
import parse from "html-react-parser";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const formatDate = (d) => (d ? moment(d).format("DD MMM YYYY") : null);
const safeLower = (s) => (s || "").toString().toLowerCase().trim();

const positionColor = (name = "") => {
  const key = safeLower(name);
  if (key.includes("dean")) return "purple";
  if (key.includes("hod") || key.includes("head")) return "magenta";
  if (key.includes("prof")) return "geekblue";
  if (key.includes("lecturer")) return "green";
  if (key.includes("assistant") || key.includes("assoc")) return "volcano";
  return "gold";
};

const initialsOf = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

const StaffTab = ({ institution }) => {
  const { staff = [] } = institution || {};

  // UI state
  const [selectedPosition, setSelectedPosition] = useState("All");
  const [sortOrder, setSortOrder] = useState("asc");
  const [dateRange, setDateRange] = useState(null); // [moment, moment]
  const [q, setQ] = useState("");

  // Unique positions for filter
  const positionOptions = useMemo(() => {
    const unique = new Set(staff.map((m) => m.position?.name).filter(Boolean));
    return ["All", ...Array.from(unique)];
  }, [staff]);

  // Filter + sort + search
  const filteredStaff = useMemo(() => {
    let rows = [...staff];

    // Position
    if (selectedPosition !== "All") {
      rows = rows.filter((m) => m.position?.name === selectedPosition);
    }

    // Date range (by date_started OR any overlap if there is an end date)
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      rows = rows.filter((m) => {
        const ds = m.date_started ? moment(m.date_started) : null;
        const de = m.date_ended ? moment(m.date_ended) : null;

        // If both exist, check overlap with range.
        if (ds && de) {
          return (
            ds.isSameOrBefore(end, "day") && de.isSameOrAfter(start, "day")
          );
        }
        // If only started date, check that it falls in range
        if (ds && !de) {
          return ds.isBetween(start, end, "day", "[]");
        }
        return false;
      });
    }

    // Search by name, title, position
    if (q.trim() !== "") {
      const needle = safeLower(q);
      rows = rows.filter((m) => {
        const name = safeLower(m.full_name);
        const title = safeLower(m?.title?.name);
        const pos = safeLower(m?.position?.name);
        return (
          name.includes(needle) ||
          title.includes(needle) ||
          pos.includes(needle)
        );
      });
    }

    // Sort by name
    rows.sort((a, b) => {
      const A = safeLower(a.full_name);
      const B = safeLower(b.full_name);
      return sortOrder === "asc" ? A.localeCompare(B) : B.localeCompare(A);
    });

    return rows;
  }, [staff, selectedPosition, dateRange, q, sortOrder]);

  const header = (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        <Title level={4} style={{ margin: 0 }}>
          Staff
        </Title>
        <Tag color="blue">{filteredStaff.length}</Tag>
        <Text type="secondary">of {staff.length} total</Text>
      </div>

      <Space wrap size="middle">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search name, title, position"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ width: 260 }}
        />

        <Select
          value={selectedPosition}
          onChange={setSelectedPosition}
          style={{ width: 200 }}
        >
          {positionOptions.map((position) => (
            <Option key={position} value={position}>
              {position}
            </Option>
          ))}
        </Select>

        <Select
          value={sortOrder}
          onChange={setSortOrder}
          style={{ width: 150 }}
        >
          <Option value="asc">A → Z</Option>
          <Option value="desc">Z → A</Option>
        </Select>

        <RangePicker
          allowEmpty={[true, true]}
          value={dateRange}
          onChange={setDateRange}
          placeholder={["Start date", "End date"]}
        />
      </Space>
    </div>
  );

  return (
    <div className="pt-5 pb-6">
      {header}

      {filteredStaff.length === 0 ? (
        <Empty description="No staff match the selected filters." />
      ) : (
        <List
          grid={{
            gutter: 24,
            xs: 1,
            sm: 2,
            md: 2,
            lg: 3,
            xl: 3,
            xxl: 4,
          }}
          dataSource={filteredStaff}
          renderItem={(member) => {
            const name = `${member?.title?.name || ""} ${
              member?.full_name || ""
            }`.trim();
            const pos = member?.position?.name;
            const started = formatDate(member?.date_started);
            const ended = formatDate(member?.date_ended);
            const tenure =
              started && (ended || "Present")
                ? `${started} — ${ended || "Present"}`
                : started || ended || "—";
            const contact = member?.contact;
            const email = member?.email;

            return (
              <List.Item key={member.id}>
                <Card
                  hoverable
                  className="transition-all duration-300 border !border-gray-200 !shadow-none !hover:shadow !hover:shadow-sm"
                  bodyStyle={{ paddingTop: 12 }}
                  title={
                    <div className="flex items-start justify-between gap-2">
                      <Space align="start">
                        <Avatar size={40} icon={<UserOutlined />}>
                          {initialsOf(member?.full_name)}
                        </Avatar>
                        <div>
                          <div className="font-medium text-gray-900">
                            {name || "—"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {member?.department?.name ||
                              member?.faculty?.name ||
                              ""}
                          </div>
                        </div>
                      </Space>

                      {pos && (
                        <Tag
                          color={positionColor(pos)}
                          style={{ marginTop: 4 }}
                        >
                          {pos}
                        </Tag>
                      )}
                    </div>
                  }
                >
                  <div className="space-y-10">
                    {/* Quick facts */}
                    <div className="space-y-2">
                      <Space size="small" wrap>
                        <Tag icon={<CalendarOutlined />} color="processing">
                          {tenure}
                        </Tag>
                        {contact && (
                          <Tooltip title={`Call ${contact}`}>
                            <Tag icon={<PhoneOutlined />} color="success">
                              {contact}
                            </Tag>
                          </Tooltip>
                        )}
                        {email && (
                          <Tooltip title={`Email ${email}`}>
                            <Tag icon={<MailOutlined />} color="blue">
                              {email}
                            </Tag>
                          </Tooltip>
                        )}
                      </Space>
                    </div>

                    {/* Details (collapsed via simple sections) */}
                    {member?.research_interests && (
                      <>
                        <Divider style={{ margin: "10px 0" }} />
                        <Title level={5} style={{ marginBottom: 6 }}>
                          Research Interests
                        </Title>
                        <div className="text-sm text-gray-800 leading-relaxed">
                          {parse(member.research_interests)}
                        </div>
                      </>
                    )}

                    {member?.qualifications && (
                      <>
                        <Divider style={{ margin: "10px 0" }} />
                        <Title level={5} style={{ marginBottom: 6 }}>
                          Qualifications
                        </Title>
                        <div className="text-sm text-gray-800 leading-relaxed">
                          {parse(member.qualifications)}
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );
};

export default StaffTab;
